import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Heart, TrendingUp, Package, Wallet, Users, MessageCircle, AlertCircle, Check } from "lucide-react";

export default function NenisPlaceTab({ tiendaId }) {
  const [productos, setProductos] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [stats, setStats] = useState(null);
  const [tienda, setTienda] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");
  const [savingWa, setSavingWa] = useState(false);

  const load = async () => {
    try {
      const [p, o, s] = await Promise.all([
        api.get("/productos", { params: { tienda_id: tiendaId } }),
        api.get("/tiendas/nenis-ordenes"),
        api.get("/tiendas/nenis-stats"),
      ]);
      setProductos(p.data.productos);
      setOrdenes(o.data.ordenes);
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    try {
      const t = await api.get(`/tiendas/${tiendaId}`);
      setTienda(t.data);
      setWhatsapp(t.data.whatsapp_gerente || "");
    } catch { /* ignore, initial state ok */ }
  };

  useEffect(() => { load(); }, [tiendaId]); // eslint-disable-line

  const tieneWhatsapp = !!(tienda?.whatsapp_gerente);

  const saveWhatsapp = async () => {
    if (!whatsapp || whatsapp.replace(/\D/g, "").length < 8) {
      toast.error("Ingresa un número de WhatsApp válido (con lada)");
      return;
    }
    setSavingWa(true);
    try {
      await api.patch(`/tiendas/${tiendaId}/perfil`, { whatsapp_gerente: whatsapp });
      toast.success("WhatsApp guardado — las NENIS ya pueden contactarte");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingWa(false); }
  };

  const toggleParticipa = async (prod, checked) => {
    if (checked && !tieneWhatsapp) {
      toast.error("Primero configura tu WhatsApp para coordinar con las NENIS");
      return;
    }
    try {
      await api.patch(`/productos/${prod.id}`, {
        participa_nenis: checked,
        precio_base_nenis: prod.precio_base_nenis || prod.precio,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updatePrecio = async (prod, precio_base) => {
    try {
      await api.patch(`/productos/${prod.id}`, { precio_base_nenis: parseFloat(precio_base) || 0 });
      toast.success("Precio base actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const avanzar = async (orden, estado) => {
    try {
      await api.patch(`/nenis-ordenes/${orden.id}/estado`, { estado });
      toast.success(`Orden marcada como ${estado.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#FF3366] to-[#E62958] text-white rounded-sm p-6">
        <div className="flex items-start gap-4">
          <Heart className="w-8 h-8 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-black text-2xl tracking-tighter">NENIS PLACE — Dropshipping para tu comercio</div>
            <p className="mt-2 text-sm text-white/90 max-w-2xl leading-relaxed">
              Cientos de <b>NENIS</b> (vendedoras independientes) publican TUS productos en Facebook, WhatsApp e Instagram, cierran ventas por ti y el repartidor del mall entrega. Tú solo defines el <b>precio base</b> que quieres recibir. Todo lo que la NENI cobre por encima, es su comisión. <b>Cero inversión adicional</b>.
            </p>
          </div>
        </div>
      </div>

      <div className={`rounded-sm p-6 border-2 ${tieneWhatsapp ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/60"}`}>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 shrink-0 rounded-sm grid place-items-center ${tieneWhatsapp ? "bg-emerald-500" : "bg-amber-500"} text-white`}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-black text-xl tracking-tighter">Tu WhatsApp de contacto</div>
              {tieneWhatsapp
                ? <Badge className="bg-emerald-500 text-white border-0"><Check className="w-3 h-3 mr-1" /> Activo</Badge>
                : <Badge className="bg-amber-500 text-white border-0"><AlertCircle className="w-3 h-3 mr-1" /> Obligatorio</Badge>}
            </div>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed max-w-3xl">
              <b>Este número es indispensable</b> para operar en NENIS PLACE. Las vendedoras necesitan contactarte directamente por WhatsApp para:
            </p>
            <ul className="mt-2 grid md:grid-cols-2 gap-1.5 text-sm text-zinc-700 max-w-3xl">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Negociar precios especiales por volumen o cliente frecuente</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Acordar descuentos y comisiones extra en promociones puntuales</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Confirmar disponibilidad de tallas, colores o stock</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span>Coordinar horarios de recolección con el repartidor</span></li>
            </ul>
            <p className="text-xs text-zinc-500 mt-3 max-w-3xl">
              Incluye lada del país. Ej: <span className="font-mono">+52 155 1234 5678</span>. Solo se muestra a NENIS registradas, nunca a clientes finales.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 max-w-md">
              <Input
                data-testid="wa-gerente-input"
                value={whatsapp}
                onChange={e => setWhatsapp(e.target.value)}
                placeholder="+52 155 1234 5678"
                className="rounded-sm h-11 font-mono"
              />
              <Button data-testid="wa-gerente-save" onClick={saveWhatsapp} disabled={savingWa} className="rounded-sm h-11 bg-[#25D366] hover:bg-[#128C7E] text-white">
                {savingWa ? "Guardando…" : tieneWhatsapp ? "Actualizar" : "Guardar y activar"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Órdenes activas" value={stats.ordenes_activas} Icon={Package} color="text-amber-600" />
          <StatCard label="Total órdenes NENI" value={stats.total} Icon={Users} color="text-blue-600" />
          <StatCard label="Ingresos" value={`$${stats.revenue.toFixed(2)}`} Icon={TrendingUp} color="text-emerald-600" />
          <StatCard label="Pagado a NENIS" value={`$${stats.payouts.toFixed(2)}`} Icon={Wallet} color="text-[#FF3366]" />
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="p-5 border-b border-zinc-100">
          <div className="font-display font-bold tracking-tight text-lg">Productos participantes</div>
          <p className="text-sm text-zinc-500 mt-0.5">Activa qué productos quieres que las NENIS puedan revender. Define el precio base (mínimo que quieres recibir).</p>
          {!tieneWhatsapp && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5" /> Configura tu WhatsApp arriba para poder activar productos.
            </div>
          )}
        </div>
        <div className="divide-y divide-zinc-100">
          {productos.map(p => (
            <div key={p.id} data-testid={`nenis-prod-${p.id}`} className="p-4 flex items-center gap-4 flex-wrap">
              <img src={p.imagen_url} alt={p.nombre} className="w-14 h-14 rounded-sm object-cover bg-zinc-100" />
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium">{p.nombre}</div>
                <div className="text-xs text-zinc-500">Precio catálogo: ${p.precio.toFixed(2)} · Stock: {p.stock_disponible}</div>
              </div>
              {p.participa_nenis && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Base $</span>
                  <Input
                    data-testid={`nenis-base-${p.id}`}
                    type="number" step="0.01"
                    defaultValue={p.precio_base_nenis || p.precio}
                    onBlur={e => updatePrecio(p, e.target.value)}
                    className="w-24 h-9 rounded-sm text-sm"
                  />
                </div>
              )}
              <Switch
                data-testid={`nenis-toggle-${p.id}`}
                checked={p.participa_nenis || false}
                disabled={!tieneWhatsapp}
                onCheckedChange={v => toggleParticipa(p, v)}
              />
            </div>
          ))}
          {productos.length === 0 && (
            <div className="p-10 text-center text-zinc-400 text-sm">Aún no tienes productos. Crea uno primero.</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="p-5 border-b border-zinc-100">
          <div className="font-display font-bold tracking-tight text-lg">Órdenes de NENIS</div>
          <p className="text-sm text-zinc-500 mt-0.5">Las NENIS envían al repartidor con el producto. Cuando reciban el efectivo del cliente, marca como "Entregado y Cobrado".</p>
        </div>
        <div className="divide-y divide-zinc-100">
          {ordenes.length === 0 ? (
            <div className="p-10 text-center text-zinc-400 text-sm">Sin órdenes NENI todavía. Activa productos arriba para empezar.</div>
          ) : ordenes.map(o => (
            <div key={o.id} data-testid={`nenis-orden-${o.id}`} className="p-4 flex flex-wrap items-center gap-4">
              <img src={o.producto_imagen} alt={o.producto_nombre} className="w-14 h-14 rounded-sm object-cover bg-zinc-100" />
              <div className="flex-1 min-w-[220px]">
                <div className="font-medium">{o.producto_nombre}</div>
                <div className="text-xs text-zinc-500">NENI: {o.neni_nombre} · Cliente: {o.buyer_name} · {o.buyer_phone}</div>
                <div className="text-xs text-zinc-500">📍 {o.buyer_address}</div>
              </div>
              <div className="text-right text-sm">
                <div className="text-zinc-500">Base ${o.precio_base.toFixed(2)}</div>
                <div className="text-emerald-600">Comisión NENI ${o.comision.toFixed(2)}</div>
                <div className="font-display font-bold">Total ${o.precio_final.toFixed(2)}</div>
              </div>
              <Badge className={
                o.estado === "Entregado_Y_Cobrado" ? "bg-emerald-100 text-emerald-700 border-0" :
                o.estado === "Enviado" ? "bg-amber-100 text-amber-700 border-0" :
                o.estado === "Cancelada" ? "bg-red-100 text-red-700 border-0" :
                "bg-zinc-100 text-zinc-700 border-0"
              }>{o.estado.replace(/_/g, " ")}</Badge>
              <div className="flex gap-1">
                {o.estado === "Pendiente" && <Button data-testid={`nenis-dispatch-${o.id}`} size="sm" onClick={() => avanzar(o, "Enviado")} className="h-8 rounded-sm bg-zinc-900 hover:bg-[#FF3366]">Enviar</Button>}
                {o.estado === "Enviado" && <Button data-testid={`nenis-collect-${o.id}`} size="sm" onClick={() => avanzar(o, "Entregado_Y_Cobrado")} className="h-8 rounded-sm bg-emerald-500 hover:bg-emerald-600">Cobrado</Button>}
                {o.estado !== "Entregado_Y_Cobrado" && o.estado !== "Cancelada" && (
                  <Button size="sm" variant="outline" onClick={() => avanzar(o, "Cancelada")} className="h-8 rounded-sm">Cancelar</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, color }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <Icon className={`w-5 h-5 ${color}`} />
      <div className="mt-3 font-display font-black text-2xl tracking-tighter">{value}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
